/** Pinia Store para la gestión de Avisos de Privacidad relacionados con el Buró de Crédito. */
import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';
import html2pdf from 'html2pdf.js';

const $toast = useToast();

export const useAmortizationStore = defineStore('amortizatio', {
    /**
     * Estado central del store.
     */
    state: () => ({
        // Configuración del store        
        errors: null,
        //Estados de la modal
        isModalOpen: false,
        amortizationContent: null,
        logo: '/img/LOGOTIPOGOLDERGFUND.jpg',
        fileName: 'Tabla_Amortización_ANEXO_B',
        //Estados de la vista principal
        searchString: '',
        //Estado de la tabla
        amortizations: [],
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        selectedApplication: null
    }),
    /**
     * Getters para filtrar, ordenar y paginar los registro de amortizaciones.
     */
    getters: {
        filtered: (state) => {
            if (!state.searchString) return state.amortizations;

            const searchLower = state.searchString.toLowerCase();
            return state.amortizations.filter(amortization => {

                let name = amortization.prospectus?.generalInfo?.businessName === null ?
                    `${amortization.prospectus?.generalInfo?.firstName} ${amortization.prospectus?.generalInfo?.lastName} ${amortization.prospectus?.generalInfo?.mothersMaidenName}` :
                    amortization.prospectus?.generalInfo?.businessName;

                return (
                    (amortization.loanApplication?.loanApplicationsReferenceNumber?.toLowerCase().includes(searchLower)) ||
                    (amortization.prospectus?.referenceNumber?.toLowerCase().includes(searchLower)) ||
                    (amortization.quotations?.quotationsReferenceNumber?.toLowerCase().includes(searchLower)) ||
                    (name.toLowerCase().includes(searchLower)) ||
                    (amortization.personType?.peopleTypeName?.toLowerCase().includes(searchLower))
                );
            });
        },
        sorted: (state) => {
            // IMPORTANTE: Usar state.filtered como base para el ordenamiento
            let sortedList = [...state.filtered];

            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item) return '';

                            if (item.prospectus?.generalInfo?.businessName === null) {
                                return `${item.prospectus?.generalInfo?.firstName || ''} ${item.prospectus?.generalInfo?.lastName || ''} ${item.prospectus?.generalInfo?.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.prospectus?.generalInfo?.businessName || '').toLowerCase();
                            }
                        };

                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }


                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    // Manejo de valores undefined/null
                    if (aValue === undefined || aValue === null) aValue = '';
                    if (bValue === undefined || bValue === null) bValue = '';

                    // Ordenamiento para strings
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return aValue.localeCompare(bValue) * state.sortOrder;
                    }

                    // Ordenamiento para números/fechas
                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        paginated: (state) => {
            // IMPORTANTE: Usar state.sorted como base para la paginación
            if (!state.sorted || state.sorted.length === 0) return [];
            const start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sorted.slice(start, start + state.itemsPerPage);
        }
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene los datos e información de la tabla para igual la carga de la información dentro de la modal
         * http://localhost:5235/api/Amortizacion?company=1
         */
        async fetchData() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.quotationsApi.get(`/api/Amortizacion?company=${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.amortizations = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Abre el modal
         */
        async openModal() {
            this.isModalOpen = true;
        },
        /**
         * Cierra el modal y limpia estados relacionados.
         */
        async closeModal() {
            this.isModalOpen = false;
            this.selectedApplication = null;
            await this.fetchData(); //Refrescar datos de las tablas
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                $toast.error(`${getLocaleMessage('common', 'responseError')} ${error.response.data}`);
            } else if (error.request) {
                $toast.error(`${getLocaleMessage('common', 'networkError')} ${error.response.data}`);
            } else {
                $toast.error(`${getLocaleMessage('common', 'generalError')} ${error.response.data}`);
            }
        },
        /**
         * Cambia el orden de las columnas en la tabla.
         */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        /**
         * Carga los datos de una amortización existente para la generación.
         */
        generateAmortization(data) {
            // Lógica para descarga registro de amortización
            this.selectedApplication = data;
        },
        /**
         * Acción para descarga el PDF del aviso de privacidad.
         */
        async downloadPDF() {
            if (!this.amortizationContent) return;

            /**Ocultar el boton de descarga */
            const formActions = document.querySelector('.form-actions');
            if (formActions) formActions.style.display = 'none';

            const opt = {
                margin: [5, 15, 5, 15], // [top, right, bottom, left]
                filename: this.fileName,
                image: {
                    type: 'jpeg',
                    quality: 1.0
                },
                html2canvas: {
                    scale: 2,
                    logging: false,
                    useCORS: true,
                    allowTaint: false,
                    scrollX: 0,
                    scrollY: 0
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4',
                    orientation: 'portrait'
                },
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy'],
                    avoid: ['tr', 'td', 'th'],
                    before: '.page-break-before',
                    after: '.page-break-after',
                    avoid: ['tr', 'td', 'th']
                }
            };

            try {
                useLoadingStore().loading = true;
                await html2pdf().set(opt).from(this.amortizationContent).save();
            } catch (error) {
                useLoadingStore().loading = false;
                this.handleError(error);
            } finally {
                useLoadingStore().loading = false;
                /**Restableciendo diseños de origen en la modal */
                if (formActions) formActions.style.display = 'block';
            }
        },
        /**
         * Establece la referencia al contenido del amortización.
         */
        setContent(ref) {
            this.amortizationContent = ref;
        }
    }
});